/* ============================================================
   storage — transfers panel.

   Every transfer in one place, with the one thing a progress
   bar usually hides: what to do when it stops. Paused means the
   bytes already sent are still on the server and Resume picks
   up from there; failed says why in the server's own words.
   ============================================================ */

import React, { useCallback, useEffect, useState } from 'react';
import { Icon, fmtBytes, fmtEta, fmtRate } from './lib';
import { api } from './api';
import { TrimJobs } from './Trim';

/**
 * What the SERVER knows is in flight, which on an admin's screen is everyone's.
 *
 * The panel above it shows this browser's own transfers — the ones it started
 * and can see the progress of directly. That is a different thing from what
 * the disk is actually receiving right now, and on a shared NAS the second
 * question is the one an admin needs: "what is filling it up" has no answer if
 * each person can only see themselves.
 */
function ServerTransfers({ token, toast, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback((signal) => api.activeUploads(token, signal)
    .then(setData)
    .catch(() => {}), [token]);

  useEffect(() => {
    const ctrl = new AbortController();
    load(ctrl.signal);
    // 5s: fast enough to watch a transfer move, slow enough to be free.
    const id = setInterval(() => { if (!document.hidden) load(); }, 5000);
    return () => { ctrl.abort(); clearInterval(id); };
  }, [load]);

  const others = (data?.uploads || []).filter((u) => !u.mine);
  const queued = data?.queued || [];
  const tot = data?.totals;
  // Only an admin sees a section; for anyone else this is the panel above,
  // repeated, and a duplicate list is worse than no list.
  if (!data?.admin || (!(data.uploads || []).length && !queued.length)) return null;

  const cancel = async (u) => {
    setBusy(u.id);
    try {
      await api.cancelUpload(token, u.id);
      toast?.('ok', 'Upload cancelled', `${u.name} · ${u.owner_name}`);
      load();
      onChanged?.();
    } catch (e) {
      toast?.('err', 'Could not cancel it', e.message);
    } finally { setBusy(null); }
  };

  return (
    <section className="st-srv">
      <div className="st-srv-head">
        <span className="label">On the server</span>
        <span className="meta">
          {data.uploads.length} in flight{others.length ? ` · ${others.length} from other people` : ''}
        </span>
      </div>
      {tot && (
        <div className="st-srv-totals tnum">
          <span><b>{tot.uploading}</b> uploading · {fmtBytes(tot.uploading_left)} left of {fmtBytes(tot.uploading_bytes)}</span>
          {tot.queued > 0 && <span><b>{tot.queued}</b> queued · {fmtBytes(tot.queued_bytes)}</span>}
          <span>{fmtBytes(tot.uploading_left + tot.queued_bytes)} to come{tot.rate > 0 ? ` · ${fmtRate(tot.rate)}` : ''}
            {tot.rate > 0 ? ` · ${fmtEta((tot.uploading_left + tot.queued_bytes) / tot.rate)}` : ''}</span>
        </div>
      )}
      <ul className="st-srv-list">
        {data.uploads.map((u) => {
          const pct = u.size > 0 ? Math.min(100, (u.received / u.size) * 100) : 0;
          return (
            <li key={u.id} className={`st-srv-row ${u.stalled ? 'is-stalled' : ''}`}>
              <div className="st-srv-top">
                <span className="st-srv-name" title={`${u.path === '/' ? '' : u.path} · ${u.name}`}>{u.name}</span>
                <span className="meta st-srv-who">{u.mine ? 'you' : u.owner_name}</span>
                <button type="button" className="iconbtn" title="Cancel this upload"
                        aria-label={`Cancel ${u.name}`} disabled={busy === u.id}
                        onClick={() => cancel(u)}><Icon name="close" size={13}/></button>
              </div>
              <div className="bar">
                <span className={`fill ${u.stalled ? 'fill--warn' : ''}`} style={{ width: `${pct}%` }}/>
              </div>
              <div className="st-srv-meta meta">
                <span>{fmtBytes(u.received)} of {fmtBytes(u.size)}</span>
                <span>
                  {u.stalled
                    // Not "slow": nothing has arrived for two minutes, which
                    // usually means a closed tab rather than a bad connection.
                    ? `stalled ${Math.round(u.idle_s / 60)}m`
                    // fmtEta already says "left"; adding it again was how the
                    // panel came to read "82.2h left left".
                    : `${fmtRate(u.rate)}${u.eta ? ` · ${fmtEta(u.eta)}` : ''}`}
                </span>
              </div>
            </li>
          );
        })}
      </ul>
      {queued.length > 0 && (
        <div className="st-srv-queued">
          {queued.map((q) => (
            <details key={q.owner_id} className="st-srv-q">
              <summary>
                <span className="st-srv-name">{q.count} queued</span>
                <span className="meta st-srv-who">{q.mine ? 'you' : q.owner_name}</span>
                <span className="meta tnum">{fmtBytes(q.bytes)}</span>
              </summary>
              <ul className="st-srv-qlist">
                {q.items.map((it, i) => (
                  <li key={i}><span className="st-srv-name" title={`${it.path} · ${it.name}`}>{it.name}</span>
                    <span className="meta tnum">{fmtBytes(it.size)}</span></li>
                ))}
                {q.count > q.items.length && <li className="meta">and {q.count - q.items.length} more</li>}
              </ul>
            </details>
          ))}
          <p className="meta st-srv-note">Queued files are still in the uploader's browser; they are reported every few seconds and vanish if the tab closes.</p>
        </div>
      )}
    </section>
  );
}

const STATE_LABEL = {
  queued: 'waiting', running: '', retrying: 'reconnecting', paused: 'paused',
  failed: 'failed', done: 'done', cancelled: 'cancelled', 'handed-off': 'downloading in your browser',
};

function Row({ item, onCancel, onRetry }) {
  const pct = item.size > 0 ? Math.min(100, (item.sent / item.size) * 100) : 0;
  const live = ['running', 'retrying', 'queued'].includes(item.state);
  const bad = item.state === 'failed';
  const paused = item.state === 'paused';
  return (
    <li className={`st-tr ${bad ? 'is-bad' : ''} ${paused ? 'is-paused' : ''}`}>
      <div className="st-tr-top">
        <Icon name={item.kind === 'upload' ? 'upload' : 'download'} size={14}/>
        <span className="st-tr-name" title={item.name}>{item.name}</span>
        {item.route === 'direct' && (
          <span className="st-tr-route" title="Straight between this device and the drive's own box, over the local network — not through the internet">direct · LAN</span>
        )}
        <span className="meta st-tr-state">{item.verifying && item.state === 'running' ? 'checking' : (STATE_LABEL[item.state] ?? item.state)}</span>
      </div>

      {(live || paused) && item.size > 0 && (
        <div className="bar st-tr-bar"><div className={`fill ${paused ? 'fill--warn' : ''}`} style={{ width: `${pct}%` }}/></div>
      )}

      <div className="st-tr-meta">
        <span className="tnum">{fmtBytes(item.sent || 0)}{item.size ? ` / ${fmtBytes(item.size)}` : ''}</span>
        {item.state === 'running' && item.rate > 0 && <span className="meta">{fmtRate(item.rate)}</span>}
        {item.state === 'running' && item.eta > 0 && <span className="meta">{fmtEta(item.eta)}</span>}
        {item.error && <span className="st-tr-err">{item.error}</span>}
      </div>

      <div className="st-tr-actions">
        {(live || paused) && (
          <button type="button" className="btn btn--ghost btn--sm" onClick={() => onCancel(item.id)}>Cancel</button>
        )}
        {(bad || paused || item.state === 'cancelled') && item.kind === 'upload' && item.file && (
          <button type="button" className="btn btn--sm" onClick={() => onRetry(item.id)}>
            {paused ? 'Resume' : 'Try again'}
          </button>
        )}
        {(bad || item.state === 'cancelled') && item.kind === 'download' && item.fileRef && (
          <button type="button" className="btn btn--sm" onClick={() => onRetry(item.id)}>Try again</button>
        )}
      </div>
    </li>
  );
}

export default function TransferPanel({ items, active, limits, token, toast, onChanged, onCancel, onRetry, onCancelAll, onClear, onClose, trim }) {
  const hasTrims = (trim?.jobs || []).length > 0;
  const done = items.filter((i) => !['queued', 'running', 'retrying', 'paused'].includes(i.state));
  return (
    <aside className="st-panel" aria-label="Transfers">
      <div className="st-panel-head">
        <span className="label">Transfers</span>
        {/* Slow is policy here, not a fault: the server paces downloads so a
            multi-gigabyte file cannot take the whole uplink out from under the
            game servers. Saying so costs one line and saves the assumption
            that something is broken. */}
        {/* Downloads only — it sat here during uploads too and read as an
            upload limit ("rate limited"), which uploads have none of. */}
        {limits?.download_bps > 0 && items.some((i) => i.kind === 'download' && ['queued', 'running', 'retrying'].includes(i.state)) && (
          <span className="meta st-tr-cap" title={`Downloads are limited to ${fmtRate(limits.download_bps)} so large transfers do not take the whole uplink. The first ${fmtBytes(limits.free_bytes)} of each file runs flat out.`}>
            downloads ≤ {fmtRate(limits.download_bps)}
          </span>
        )}
        <div className="st-panel-head-actions">
          {active.length > 0 && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={onCancelAll}>Cancel all</button>
          )}
          {done.length > 0 && (
            <button type="button" className="btn btn--ghost btn--sm" onClick={onClear}>Clear finished</button>
          )}
          <button type="button" className="iconbtn" onClick={onClose} aria-label="Close transfers"><Icon name="close" size={14}/></button>
        </div>
      </div>

      {items.length === 0 ? (!hasTrims && (
        <div className="empty st-empty-sm">
          Nothing transferring. Uploads, downloads and trims show up here with progress, and stay if they fail.
        </div>
      )) : (
        <ul className="st-tr-list">
          {items.map((i) => <Row key={i.id} item={i} onCancel={onCancel} onRetry={onRetry}/>)}
        </ul>
      )}
      <TrimJobs trim={trim}/>
      <ServerTransfers token={token} toast={toast} onChanged={onChanged}/>
    </aside>
  );
}
